import { Transition } from "@headlessui/react"
import { Form, Head, usePage } from "@inertiajs/react"

import DeleteUser from "@/components/delete-user"
import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useInitials } from "@/hooks/use-initials"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { settingsProfilePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Perfil",
    href: settingsProfilePath(),
  },
]

export default function Profile() {
  const { auth } = usePage().props
  const getInitials = useInitials()

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title="Foto de perfil"
            description="Tu foto se muestra en la navegación, publicaciones y perfil"
          />

          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage
                src={auth.user.avatar_url ?? undefined}
                alt={auth.user.full_name}
                className="object-cover"
              />
              <AvatarFallback className="bg-krown-200 text-lg font-bold text-white">
                {getInitials(auth.user.full_name)}
              </AvatarFallback>
            </Avatar>
            <p className="text-muted-foreground text-sm">
              Para cambiar tu foto de perfil, editá tu perfil.
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <HeadingSmall
            title="Información del perfil"
            description="Actualizá tu nombre"
          />

          <Form
            method="patch"
            action={settingsProfilePath()}
            options={{
              preserveScroll: true,
            }}
            className="space-y-6"
          >
            {({ errors, processing, recentlySuccessful }) => (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="first_name">Nombre</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      className="mt-1 block w-full"
                      defaultValue={auth.user.first_name}
                      required
                      autoComplete="given-name"
                      placeholder="Nombre"
                    />
                    <InputError className="mt-2" messages={errors.first_name} />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="last_name">Apellido</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      className="mt-1 block w-full"
                      defaultValue={auth.user.last_name}
                      required
                      autoComplete="family-name"
                      placeholder="Apellido"
                    />
                    <InputError className="mt-2" messages={errors.last_name} />
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <Button disabled={processing}>Guardar</Button>

                  <Transition
                    show={recentlySuccessful}
                    enter="transition ease-in-out"
                    enterFrom="opacity-0"
                    leave="transition ease-in-out"
                    leaveTo="opacity-0"
                  >
                    <p className="text-muted-foreground text-sm">Guardado</p>
                  </Transition>
                </div>
              </>
            )}
          </Form>
        </div>

        <DeleteUser />
      </SettingsLayout>
    </AppLayout>
  )
}
